import React, { useEffect, useState, useMemo, useRef } from 'react';
import { FunnelIcon, ChevronLeftIcon, ChevronRightIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import styles from '../../styles/component.module.scss';
import pageStyles from '../../styles/aICostTracking/AICostTrackingPage.module.scss';
import drawerStyles from '../../styles/aICostTracking/TokenCostSettingsDrawer.module.scss';
import PageHeader from '../Header/PageHeader';
import { useSelector } from 'react-redux';
import CostAnalysisTable from './CostAnalysisTable';
import { getCostAnalysisOrgWiseData, getCostAnalysisUsageInfo, getCostAnalysisProjectUserUsageInfo, fetchFilterList, updateModelTokenCosts, downloadCostTrackingCSV } from '../../utils/api/costAnalysisApis';
import { STATUS } from '../../utils/api/sharedfunction';
import CostAnalysisGraph from './CostAnalysisGraph';
import { getOrganizations } from '../../utils/api/organization_apis';
import CostAnalysisFilter from './CostAnalysisFilter';
import TokenCostSettingsDrawer from './TokenCostSettingsDrawer';
// import { toast } from 'react-toastify';

const AICostTracking = () => {
    const activePage = useSelector((state) => state.navigation.activePage);
    const [costAnalysisUsageInfo, setCostAnalysisUsageInfo] = useState(null);
    const [costAnalysisPagination, setCostAnalysisPagination] = useState(null);
    const [costAnalysisOrgWiseData, setCostAnalysisOrgWiseData] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedOrgId, setSelectedOrgId] = useState('');
    const [projectUserUsageInfo, setProjectUserUsageInfo] = useState(null);
    const [filters, setFilters] = useState({});
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(true); // Open by default
    const [isCollapsed, setIsCollapsed] = useState(false); // Collapsed state
    const [activeFilterChips, setActiveFilterChips] = useState([]); // Store active filter chips with labels
    const [filterListData, setFilterListData] = useState(null); // Store filter list data from API
    const [tokenCostDrawerOpen, setTokenCostDrawerOpen] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [downloadingCSV, setDownloadingCSV] = useState(false);

    const breadcrumbItems = [
        { label: "Administration" },
        { label: activePage}
    ];

    // Reset pagination to page 1 when filters change
    useEffect(() => {
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    }, [filters.organization, filters.project, filters.user, filters.modelversion, filters.executionmode, filters.startDate, filters.endDate]);

    // Clear errors when filters change (to prevent stale error messages)
    useEffect(() => {
        setError(null);
    }, [filters.organization, filters.project, filters.user, filters.modelversion, filters.executionmode, filters.startDate, filters.endDate]);

    useEffect(() => {
        const fetchCostAnalysisUsageInfo = async () => {
            setLoading(true);
            // Convert page from 0-based (MUI) to 1-based (API)
            const page = paginationModel.page + 1;
            const pageSize = paginationModel.pageSize;
            const response = await getCostAnalysisUsageInfo(filters, page, pageSize);
            if (response.status === STATUS.SUCCESS) {
                setCostAnalysisUsageInfo(response.data || []);
                setCostAnalysisPagination(response.pagination || {});
            } else {
                setError(response.error || 'Failed to fetch cost analysis usage info');
            }
            setLoading(false);
        }
        fetchCostAnalysisUsageInfo();
    }, [filters, paginationModel]);

    const hasInitiallyLoaded = useRef(false);

    // Fetch org-wise data on mount and when specific filters change
    useEffect(() => {
        const fetchCostAnalysisOrgWiseData = async () => {
            setLoading(true);
            const response = await getCostAnalysisOrgWiseData({ 
                organization: filters.organization, 
                modelVersions: filters.modelversion, 
                executionModes: filters.executionmode,
                startDate: filters.startDate,
                endDate: filters.endDate
            });
            if (response.status === STATUS.SUCCESS) {
                setCostAnalysisOrgWiseData(response.data || {});
            } else {
                setError(response.error || 'Failed to fetch cost analysis org wise data');
            }
            setLoading(false);
            hasInitiallyLoaded.current = true;
        }
        
        // Always fetch data - on mount and when dependencies change
        fetchCostAnalysisOrgWiseData();
    }, [filters.organization, filters.modelversion, filters.executionmode, filters.startDate, filters.endDate]);

    useEffect(() => {
        const fetchOrganizations = async () => {
            setLoading(true);
            const response = await getOrganizations();
            if (response.status === STATUS.SUCCESS) {
                setOrganizations(response.data || []);
            } else {
                setError(response.error || 'Failed to fetch organizations');
            }
            setLoading(false);
        }
        fetchOrganizations();
    }, []);

    useEffect(() => {
        const fetchProjectUserUsageInfo = async () => {
            if (!selectedOrgId) {
                setProjectUserUsageInfo(null);
                return;
            }
            setLoading(true);
            const response = await getCostAnalysisProjectUserUsageInfo({ organizationId: selectedOrgId, projectIds: filters.project, userIds: filters.user, modelVersions: filters.modelversion, executionModes: filters.executionmode, startDate: filters.startDate, endDate: filters.endDate });
            if (response.status === STATUS.SUCCESS) {
                setProjectUserUsageInfo(response.data);
            } else {
                setError(response.error || 'Failed to fetch project/user usage info');
            }
            setLoading(false);
        }
        fetchProjectUserUsageInfo();
    }, [selectedOrgId, filters.project, filters.user, filters.modelversion, filters.executionmode, filters.startDate, filters.endDate]);

    useEffect(() => {
        const fetchFilterListData = async () => {
            const projId = filters.project?.join(',') || '';
            const response = await fetchFilterList({ 
                organizationId: filters.organization, 
                projectIds: projId
            });
            if (response.status === STATUS.SUCCESS) {
                setFilterListData(response.data);
            } else {
                setError(response.error || 'Failed to fetch filter list');
            }
        }
        fetchFilterListData();
    }, [selectedOrgId, filters.organization, filters.project]);

    const organizationsOptions = useMemo(() => {
        if (!costAnalysisOrgWiseData?.length) return [];
        return costAnalysisOrgWiseData.map((row) => ({
            label: row.organizationName,
            value: row.organizationId,
        }));
    }, [costAnalysisOrgWiseData]);


    const activeFilterCount = Object.entries(filters).reduce((count, [key, value]) => {
        if (key === 'startDate' || key === 'endDate') {
            return value ? count + 1 : count;
        }
        return (Array.isArray(value) ? value.length > 0 : value !== '') ? count + 1 : count;
    }, 0);

    // Compute active filter chips from filters state (for when CostAnalysisFilter is not mounted)
    const computedFilterChips = useMemo(() => {
        const filterLabels = {
            organization: 'Organization',
            project: 'Project',
            user: 'User',
            modelversion: 'Model Version',
            executionmode: 'Execution Mode',
            startDate: 'Start Date',
            endDate: 'End Date',
        };

        const chips = [];
        Object.entries(filters).forEach(([key, value]) => {
            if (key === 'startDate' || key === 'endDate') {
                if (value) {
                    const date = new Date(value);
                    const formattedDate = date.toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                    });
                    chips.push({
                        key,
                        label: filterLabels[key] || key,
                        value: formattedDate,
                    });
                }
            } else {
                // Handle array values for multi-select
                const valueArray = Array.isArray(value) ? value : (value ? [value] : []);
                if (valueArray.length > 0) {
                    valueArray.forEach((val, index) => {
                        chips.push({
                            key: `${key}_${index}`,
                            label: filterLabels[key] || key,
                            value: String(val),
                        });
                    });
                }
            }
        });
        return chips;
    }, [filters]);

    // Use activeFilterChips from child if available, otherwise use computed chips
    const displayFilterChips = activeFilterChips.length > 0 ? activeFilterChips : computedFilterChips;

    const handleSaveTokenCosts = async (updatedCosts) => {
        try {
            const response = await updateModelTokenCosts(updatedCosts);
            if (response.status === STATUS.SUCCESS) {
                // toast.success('Token costs updated successfully');
                // Refresh data if needed
                const fetchCostAnalysisUsageInfo = async () => {
                    setLoading(true);
                    const page = paginationModel.page + 1;
                    const pageSize = paginationModel.pageSize;
                    const response = await getCostAnalysisUsageInfo(filters, page, pageSize);
                    if (response.status === STATUS.SUCCESS) {
                        setCostAnalysisUsageInfo(response.data || []);
                        setCostAnalysisPagination(response.pagination || {});
                    }
                    setLoading(false);
                }
                fetchCostAnalysisUsageInfo();
            } else {
                // toast.error(response.error || 'Failed to update token costs');
            }
        } catch (error) {
            console.error('Error in handleSaveTokenCosts:', error);
            // toast.error('An unexpected error occurred');
        }
    };

    const handleDownloadCSV = async () => {
        // Check if organization is selected
        const orgValue = Array.isArray(filters.organization) 
            ? (filters.organization.length > 0 ? filters.organization[0] : '')
            : filters.organization;
        
        if (!orgValue || orgValue.trim().length === 0) {
            return; // Button should be disabled, but double-check
        }

        // Clear any previous errors before attempting download
        setError(null);
        setDownloadingCSV(true);
        
        try {
            const response = await downloadCostTrackingCSV(filters);
            if (response.status === STATUS.SUCCESS) {
                // Download initiated successfully - no error to set
            } else {
                // Handle validation errors with detailed feedback
                let errorMessage = response.error || 'Failed to download CSV';
                
                // If there are validation errors, format them nicely
                if (response.errors && Array.isArray(response.errors) && response.errors.length > 0) {
                    const validationErrors = response.errors
                        .map(err => err.message || `${err.field}: ${err.message}`)
                        .join(', ');
                    errorMessage = `Validation error: ${validationErrors}`;
                } else if (response.statusCode === 400) {
                    errorMessage = `Validation error: ${errorMessage}`;
                } else if (response.statusCode === 403) {
                    errorMessage = `Permission denied: ${errorMessage}`;
                } else if (response.statusCode >= 500) {
                    errorMessage = `Server error: ${errorMessage}`;
                }
                
                setError(errorMessage);
            }
        } catch (error) {
            console.error('Error downloading CSV:', error);
            setError('An unexpected error occurred while downloading CSV. Please try again.');
        } finally {
            setDownloadingCSV(false);
        }
    };

    // Check if organization is selected for button disabled state
    const isOrganizationSelected = useMemo(() => {
        const orgValue = Array.isArray(filters.organization) 
            ? (filters.organization.length > 0 ? filters.organization[0] : '')
            : filters.organization;
        return orgValue && orgValue.trim().length > 0;
    }, [filters.organization]);

    // Group filter chips by label for better tooltip display
    const groupedFilterChips = useMemo(() => {
        const groups = {};
        displayFilterChips.forEach((chip) => {
            if (!groups[chip.label]) {
                groups[chip.label] = [];
            }
            groups[chip.label].push(chip.value);
        });
        return Object.entries(groups).map(([label, values]) => ({
            label,
            values
        }));
    }, [displayFilterChips]);

    return (
        <div className={`${styles['page-container']} ${pageStyles.pageContainerWithScroll}`}>
            <div>
                <PageHeader
                    projectName={null}
                    title="AI Cost Tracking"
                    description="View and manage your AI cost tracking."
                    activePage={activePage}
                    breadcrumbItems={breadcrumbItems}
                />
            </div>
            <div className={`${styles['cover-container-col-auto']} ${pageStyles.page}`}>
                <div className={pageStyles.mainLayoutContainer}>
                    {/* Main Content Area */}
                    <div className={`${pageStyles.mainContentArea} ${
                        filterDrawerOpen 
                            ? (isCollapsed ? pageStyles.withDrawerCollapsed : pageStyles.withDrawer)
                            : pageStyles.fullWidth
                    }`}>
                        {/* Filter Toggle Button - Fixed when closed */}
                        {!filterDrawerOpen && (
                            <div className={pageStyles.filterToggleContainer}>
                                <div className={pageStyles.filterToggleButtonWrapper}>
                                    <button
                                        onClick={() => setFilterDrawerOpen(true)}
                                        className={pageStyles.filterToggleButton}
                                    >
                                        <FunnelIcon style={{ width: 20, height: 20, color: '#2563EB' }} />
                                    </button>
                                    {activeFilterCount > 0 && (
                                        <div className={pageStyles.filterBadgeContainer}>
                                            <span className={pageStyles.filterBadgeInner}>
                                                {activeFilterCount}
                                            </span>
                                            <div className={pageStyles.filterBadgeTooltip}>
                                                <div className={pageStyles.filterBadgeTooltipArrow} />
                                                <div className={pageStyles.filterBadgeTooltipHeader}>
                                                    Active Filters ({activeFilterCount})
                                                </div>
                                                <div className={pageStyles.filterBadgeTooltipBody}>
                                                    {displayFilterChips.length > 0 ? (
                                                        displayFilterChips.map((chip) => (
                                                            <div key={chip.key} className={pageStyles.filterBadgeTooltipItem}>
                                                                <span className={pageStyles.filterBadgeTooltipLabel}>
                                                                    {chip.label}
                                                                </span>
                                                                <span className={pageStyles.filterBadgeTooltipValue}>
                                                                    {chip.value}
                                                                </span>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <span className={pageStyles.filterBadgeTooltipEmpty}>
                                                            No active filters
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className={`${pageStyles.content} ${pageStyles.contentWrapper}`}>
                            <div className={pageStyles.graphContainer}>
                                <CostAnalysisGraph 
                                    costAnalysisOrgWiseData={costAnalysisOrgWiseData} 
                                    selectedOrgId={selectedOrgId || (Array.isArray(filters.organization) && filters.organization.length > 0 ? filters.organization[0] : filters.organization)} 
                                    projectUserUsageInfo={projectUserUsageInfo} 
                                />
                            </div>
                            <div className={`${pageStyles.card} ${pageStyles.tableContainer}`}>
                                <CostAnalysisTable 
                                    costAnalysisUsageInfo={costAnalysisUsageInfo} 
                                    pagination={costAnalysisPagination}
                                    loading={loading} 
                                    error={error} 
                                    organizations={organizations}
                                    paginationModel={paginationModel}
                                    onPaginationModelChange={setPaginationModel}
                                    onDownloadCSV={handleDownloadCSV}
                                    downloadingCSV={downloadingCSV}
                                    isOrganizationSelected={isOrganizationSelected}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Filter Side Panel */}
                    {filterDrawerOpen && (
                        <div className={`${pageStyles.sidePanelContainer} ${isCollapsed ? pageStyles.collapsed : pageStyles.expanded}`}>
                            <div className={pageStyles.sidePanel}>
                                {/* Header */}
                                <div className={`${pageStyles.sidePanelHeader} ${isCollapsed ? pageStyles.collapsed : ''}`}>
                                    {!isCollapsed && (
                                        <div className={pageStyles.sidePanelHeaderLeft}>
                                            <FunnelIcon style={{ width: 20, height: 20, color: '#2563EB' }} />
                                            <h3 className={pageStyles.sidePanelTitle}>
                                                Filters
                                            </h3>
                                        </div>
                                    )}
                                    {isCollapsed && (
                                        <div title="Filters" className={pageStyles.sidePanelIconContainer}>
                                            <FunnelIcon style={{ width: 24, height: 24, color: '#2563EB' }} />
                                        </div>
                                    )}
                                    <div className={pageStyles.sidePanelHeaderRight}>
                                        {!isCollapsed && (
                                            <button
                                                title="Collapse"
                                                onClick={() => setIsCollapsed(true)}
                                                className={pageStyles.collapseButton}
                                            >
                                                <ChevronRightIcon style={{ width: 16, height: 16 }} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Collapsed View - Icon Bar */}
                                {isCollapsed && (
                                    <div className={pageStyles.collapsedView}>
                                        <button
                                            title="Expand Filters"
                                            onClick={() => setIsCollapsed(false)}
                                            className={pageStyles.expandButton}
                                        >
                                            <ChevronLeftIcon style={{ width: 20, height: 20 }} />
                                        </button>
                                        {activeFilterCount > 0 && (
                                            <div 
                                                className={pageStyles.collapsedFilterBadge}
                                                onMouseEnter={(e) => {
                                                    const tooltip = e.currentTarget.querySelector(`.${pageStyles.collapsedFilterTooltip}`);
                                                    if (tooltip) {
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        tooltip.style.top = `${rect.top + rect.height / 2}px`;
                                                        tooltip.style.right = `${window.innerWidth - rect.left + 12}px`;
                                                    }
                                                }}
                                            >
                                                <span className={pageStyles.collapsedFilterCount}>
                                                    {activeFilterCount}
                                                </span>
                                                <div className={pageStyles.collapsedFilterTooltip}>
                                                    <div className={pageStyles.collapsedFilterTooltipArrow} />
                                                    <div className={pageStyles.collapsedFilterTooltipHeader}>
                                                        Active Filters ({activeFilterCount})
                                                    </div>
                                                    <div className={pageStyles.collapsedFilterTooltipBody}>
                                                        {groupedFilterChips.length > 0 ? (
                                                            groupedFilterChips.map((group, index) => (
                                                                <div key={index} className={pageStyles.collapsedFilterTooltipItem}>
                                                                    <span className={pageStyles.collapsedFilterTooltipLabel}>
                                                                        {group.label}
                                                                    </span>
                                                                    <div className={pageStyles.collapsedFilterTooltipValues}>
                                                                        {group.values.map((value, idx) => (
                                                                            <span key={idx} className={pageStyles.collapsedFilterTooltipValue}>
                                                                                {value}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <span className={pageStyles.collapsedFilterTooltipEmpty}>
                                                                No active filters
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Expanded View - Full Filter Form */}
                                {!isCollapsed && (
                                    <div className={pageStyles.expandedView}>
                                        <CostAnalysisFilter 
                                            onFilterChange={(newFilters) => {
                                                setFilters(newFilters);
                                            // Update selectedOrgId when organization filter changes
                                            // For multi-select, use first selected organization
                                            const orgValue = Array.isArray(newFilters.organization) 
                                                ? (newFilters.organization.length > 0 ? newFilters.organization[0] : '')
                                                : newFilters.organization;
                                            if (orgValue !== selectedOrgId) {
                                                setSelectedOrgId(orgValue || '');
                                            }
                                            }}
                                            onActiveFiltersChange={setActiveFilterChips}
                                            initialFilters={filters}
                                            isSidePanel={true}
                                            filterListData={filterListData}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Floating Action Button for Token Costs */}
            <button 
                className={drawerStyles.floatingActionButton}
                onClick={() => setTokenCostDrawerOpen(true)}
                title="Manage Token Costs"
            >
                <BanknotesIcon className={drawerStyles.fabIcon} />
            </button>

            {/* Token Cost Settings Drawer */}
            <TokenCostSettingsDrawer 
                isOpen={tokenCostDrawerOpen}
                onClose={() => setTokenCostDrawerOpen(false)}
                onSave={handleSaveTokenCosts}
            />
        </div>
    );
};

export default AICostTracking;